import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import usePartnersWithMethodOfContact from "@/modules/common/hooks/partners-with-method-of-contact-fetching.hook";
import { OptionProps, Options } from "@/modules/common/types";
import { EditMethodOfContactProps } from "@/modules/lead-page/types";
import { Partner } from "@/modules/leads/types";
import { Box } from "@mui/material";
import { UUID } from "crypto";
import { useCallback, useEffect, useMemo, useState } from "react";

const EditMethodOfContact = ({
  methodOfContact,
  lead,
  autoFocus,
}: EditMethodOfContactProps) => {
  const [selectedMethod, setSelectedMethod] = useState<OptionProps | null>(
    null
  );
  const { partnersData, partnersLoading, setPartnersSearch } =
    usePartnersWithMethodOfContact(methodOfContact.state as UUID);

  const options: OptionProps[] = useMemo(() => {
    return (
      methodOfContact.data?.data?.data?.results?.map(
        (option: { uid: UUID; name: string }) => ({
          value: option.uid,
          label: option.name,
        })
      ) || []
    );
  }, [methodOfContact.data]);

  const partnersOptions: Options[] = useMemo(() => {
    return (
      partnersData?.data?.map((option: Partner) => ({
        value: option.uid,
        label: `${option.name} - ${option.partnerOrganization?.name}`,
        phoneNumber: option.phone,
      })) ?? []
    );
  }, [partnersData]);

  useEffect(() => {
    const currentMethodId =
      methodOfContact.value ?? lead?.data.lead.method_of_contact?.id;
    const currentMethod = options.find(
      (option) => option.value === currentMethodId
    );
    if (
      currentMethod &&
      (!selectedMethod || currentMethod.value !== selectedMethod.value)
    ) {
      setSelectedMethod(currentMethod);
    }
  }, [
    methodOfContact.value,
    lead?.data.lead.method_of_contact?.id,
    options,
    selectedMethod,
  ]);

  const handleChange = useCallback(
    (value: string | null) => {
      const methodId = value;
      const selectedOption = options.find(
        (option) => option.value === methodId
      );
      if (selectedOption) {
        setSelectedMethod(selectedOption);
        methodOfContact.handleChangeMethodOfContact(methodId as UUID);
      }
    },
    [methodOfContact, options]
  );

  const handlePartnerChange = useCallback(
    (value: string | null) => {
      if (methodOfContact.setSelectedMethodOfContactPartner) {
        methodOfContact.setSelectedMethodOfContactPartner(value as UUID);
      }
    },
    [methodOfContact]
  );

  const handlePartnerSearch = useCallback(
    (value: string) => {
      const searchTerm = value.split("-")[0]?.trim() ?? "";
      setPartnersSearch(searchTerm);
    },
    [setPartnersSearch]
  );

  const shouldShowPartnerSelect = partnersOptions.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <SingleSelectSearch
        options={options}
        size="medium"
        label="Method of Contact"
        onValueChange={handleChange}
        searchValue={methodOfContact.search}
        onSearchValueChange={methodOfContact.setSearch}
        isLoading={methodOfContact.isLoading}
        autoFocus={autoFocus}
      />

      {shouldShowPartnerSelect && (
        <Box sx={{ width: "100%" }}>
          <SingleSelectSearch
            options={partnersOptions}
            noOptionsText="No partners for this method of contact"
            onValueChange={handlePartnerChange}
            value={methodOfContact.selectedPartner}
            label="Select Partner"
            onSearchValueChange={handlePartnerSearch}
            isLoading={partnersLoading}
          />
        </Box>
      )}
    </Box>
  );
};

export default EditMethodOfContact;
